import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  merge,
  Observable
} from 'rxjs';

export enum DEVICE_TYPE {
  MOBILE = 'MOBILE',
  TABLET = 'TABLET',
  DESKTOP_LOW_RESOLUTION = 'DESKTOP_LOW_RESOLUTION',
  DESKTOP_HIGH_RESOLUTION = 'DESKTOP_HIGH_RESOLUTION'
}
export interface DeviceInfo {
  isMobile: boolean;
  deviceType: DEVICE_TYPE | string;
  screenWidth: number;
  screenHeight: number;
  hasTouchScreen: boolean;
  orientation: 'portrait' | 'landscape';
}

@Injectable({
  providedIn: 'root'
})
export class ScreenService {
  private deviceInfoSubject = new BehaviorSubject<DeviceInfo>(
    this.detectDeviceInfo()
  );

  // Rastreia se estamos em um evento de rolagem
  private isScrolling = false;
  private scrollTimer: any = null;

  constructor() {
    // Função para verificar e atualizar as informações de dispositivo
    const updateInfo = () => {
      // Não atualizar durante rolagem
      if (this.isScrolling) {
        return;
      }

      const newInfo = this.detectDeviceInfo();
      const prevInfo = this.deviceInfoSubject.value;

      // Apenas enviar atualizações se houve mudança real no tipo de dispositivo ou orientação
      if (
        prevInfo.deviceType !== newInfo.deviceType ||
        prevInfo.isMobile !== newInfo.isMobile ||
        prevInfo.orientation !== newInfo.orientation
      ) {
        this.deviceInfoSubject.next(newInfo);
      }
    };

    // Detectar eventos de scroll para ignorá-los
    fromEvent(window, 'scroll').subscribe(() => {
      this.isScrolling = true;

      // Limpar timer existente
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer);
      }

      // Definir um timer para desmarcar o estado de rolagem após terminar
      this.scrollTimer = setTimeout(() => {
        this.isScrolling = false;
      }, 500);
    });

    // Apenas responder a eventos legítimos de mudança de layout
    merge(fromEvent(window, 'resize'), fromEvent(window, 'orientationchange'))
      .pipe(
        debounceTime(500),
        filter(() => !this.isScrolling) // Não processar durante rolagem
      )
      .subscribe(updateInfo);

    // Inicialização única ao carregar a página
    setTimeout(updateInfo, 100);
  }

  public deviceType(): Observable<DeviceInfo> {
    return this.deviceInfoSubject.asObservable().pipe(
      distinctUntilChanged(
        (prev, curr) =>
          prev.deviceType === curr.deviceType &&
          prev.isMobile === curr.isMobile &&
          prev.orientation === curr.orientation
        // Não comparar dimensões exatas, apenas os fatores críticos
      )
    );
  }

  public getCurrentDeviceInfo(): DeviceInfo {
    return this.deviceInfoSubject.value;
  }

  public detectDeviceInfo(): DeviceInfo {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const orientation: 'portrait' | 'landscape' =
      screenWidth > screenHeight ? 'landscape' : 'portrait';
    const hasTouchScreen =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;

    const isMobileUA = this.checkUserAgentForMobile();
    const breakpoint = this.getBreakpoint();

    let deviceType: string;
    let isMobile: boolean;

    if (breakpoint === 'xs') {
      deviceType = DEVICE_TYPE.MOBILE;
      isMobile = true;
    } else if (breakpoint === 'sm' || breakpoint === 'md') {
      deviceType = DEVICE_TYPE.TABLET;
      isMobile = true;
    } else if (breakpoint === 'lg') {
      deviceType = DEVICE_TYPE.DESKTOP_LOW_RESOLUTION;
      isMobile = false;
    } else if (breakpoint === 'xl') {
      deviceType = DEVICE_TYPE.DESKTOP_HIGH_RESOLUTION;
      isMobile = false;
    } else {
      isMobile = screenWidth < 768 || hasTouchScreen || isMobileUA;
      deviceType = isMobile ? 'MOBILE' : 'DESKTOP';
    }

    return {
      isMobile,
      deviceType,
      screenWidth,
      screenHeight,
      hasTouchScreen,
      orientation
    };
  }

  private getBreakpoint(): string {
    const breakpoints: { [key: string]: string } = {
      xs: '(max-width: 599.98px)',
      sm: '(min-width: 600px) and (max-width: 959.98px)',
      md: '(min-width: 960px) and (max-width: 1279.98px)',
      md_landscape:
        '(min-width: 960px) and (max-width: 1279.98px) and (orientation: landscape)',
      lg: '(min-width: 1280px) and (orientation: landscape)',
      xl: '(min-width: 1920px)'
    };

    if (matchMedia(breakpoints.xs).matches) return 'xs';
    if (matchMedia(breakpoints.sm).matches) return 'sm';
    if (
      matchMedia(breakpoints.md).matches ||
      matchMedia(breakpoints.md_landscape).matches
    )
      return 'md';
    if (
      matchMedia(breakpoints.lg).matches &&
      !matchMedia(breakpoints.xl).matches
    )
      return 'lg';
    if (matchMedia(breakpoints.xl).matches) return 'xl';
    return 'unknown';
  }

  private checkUserAgentForMobile(): boolean {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    const screenId = ua.toLowerCase();

    return (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|ipad|playbook|silk/i.test(
        screenId
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        screenId.slice(0, 4)
      ) ||
      /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
        ua
      ) ||
      /(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)
    );
  }
}
